import React, { useState, useEffect, useCallback } from 'react';
import { getClientSiteAddresses, createSiteAddress, updateSiteAddress, deleteSiteAddress } from '../../services/siteAddresses';
import './SiteAddressesTab.css';

export default function SiteAddressesTab({ clientId, client }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    address_name: '',
    is_primary: false,
    notes: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadAddresses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClientSiteAddresses(clientId);
      setAddresses(data);
    } catch (error) {
      console.error('Error loading site addresses:', error);
      setError('Failed to load site addresses: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.address_line1 || !formData.city || !formData.state || !formData.zip_code) {
      setError('Please fill in all required address fields');
      return;
    }

    try {
      if (editingAddress) {
        await updateSiteAddress(editingAddress.id, {
          ...formData,
          client_id: clientId,
        });
        setSuccess('Site address updated successfully!');
      } else {
        await createSiteAddress({
          ...formData,
          client_id: clientId,
        });
        setSuccess('Site address added successfully!');
      }

      await loadAddresses();
      setShowAddModal(false);
      setEditingAddress(null);
      setFormData({
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'USA',
        address_name: '',
        is_primary: false,
        notes: '',
      });
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      setError(error.message || 'Failed to save site address');
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      address_line1: address.address_line1,
      address_line2: address.address_line2 || '',
      city: address.city,
      state: address.state,
      zip_code: address.zip_code,
      country: address.country || 'USA',
      address_name: address.address_name || '',
      is_primary: address.is_primary,
      notes: address.notes || '',
    });
    setShowAddModal(true);
  };

  const handleDelete = async (addressId) => {
    if (window.confirm('Are you sure you want to delete this site address?')) {
      try {
        await deleteSiteAddress(addressId);
        await loadAddresses();
        setSuccess('Site address deleted successfully!');
        setTimeout(() => setSuccess(''), 2000);
      } catch (error) {
        setError('Failed to delete site address: ' + error.message);
      }
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingAddress(null);
    setFormData({
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      address_name: '',
      is_primary: false,
      notes: '',
    });
    setError('');
    setSuccess('');
  };

  const handleCopyClientAddress = () => {
    if (!client) return;
    
    // Parse the client's address - it's stored as a single string in client.address
    // We'll try to split it intelligently, but if it fails, just use it as address_line1
    let addressLine1 = client.address || '';
    let addressLine2 = '';
    
    // Try to detect if address contains line breaks or common separators
    if (addressLine1.includes('\n')) {
      const lines = addressLine1.split('\n').filter(line => line.trim());
      addressLine1 = lines[0] || '';
      addressLine2 = lines.slice(1).join(' ') || '';
    } else if (addressLine1.includes(',')) {
      // If it's comma-separated, try to split intelligently
      const parts = addressLine1.split(',').map(p => p.trim());
      if (parts.length > 1) {
        addressLine1 = parts[0];
        addressLine2 = parts.slice(1).join(', ');
      }
    }
    
    setFormData({
      ...formData,
      address_line1: addressLine1,
      address_line2: addressLine2,
      city: client.city || '',
      state: client.state || '',
      zip_code: client.zip || '',
      country: 'USA',
    });
  };

  const formatAddress = (address) => {
    const parts = [
      address.address_line1,
      address.address_line2,
      `${address.city}, ${address.state} ${address.zip_code}`,
      address.country !== 'USA' ? address.country : null,
    ].filter(Boolean);
    return parts.join('\n');
  };

  if (loading) {
    return <div className="loading-state">Loading site addresses...</div>;
  }

  return (
    <div className="site-addresses-tab">
      <div className="section-header">
        <h2>Site Addresses</h2>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          + Add Site Address
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {addresses.length === 0 ? (
        <div className="empty-state">
          <p>No site addresses added yet. Add your first site address to get started.</p>
        </div>
      ) : (
        <div className="addresses-list">
          {addresses.map((address) => (
            <div key={address.id} className={`address-card ${address.is_primary ? 'primary-address' : ''}`}>
              <div className="address-header">
                <div className="address-info">
                  <h3>{address.address_name || 'Unnamed Address'}</h3>
                  {address.is_primary && (
                    <span className="primary-badge">Primary</span>
                  )}
                </div>
                <div className="address-actions">
                  <button className="btn-edit" onClick={() => handleEdit(address)}>
                    Edit
                  </button>
                  <button className="btn-delete" onClick={() => handleDelete(address.id)}>
                    Delete
                  </button>
                </div>
              </div>
              <div className="address-details">
                <pre className="address-text">{formatAddress(address)}</pre>
                {address.notes && (
                  <div className="address-notes">
                    <span className="notes-label">Notes:</span>
                    <span className="notes-value">{address.notes}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="site-address-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAddress ? 'Edit Site Address' : 'Add New Site Address'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="site-address-modal-body">
                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}

                {!editingAddress && client && (client.address || client.city || client.state || client.zip) && (
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn-copy-address"
                      onClick={handleCopyClientAddress}
                    >
                      📋 Copy Client Address
                    </button>
                    <small className="form-hint">
                      Copy the client's main address information to pre-fill this form.
                    </small>
                  </div>
                )}

                <div className="form-group">
                  <label>Address Name (Optional)</label>
                  <input
                    type="text"
                    autoComplete="off"
                    name="address-name"
                    value={formData.address_name}
                    onChange={(e) => setFormData({ ...formData, address_name: e.target.value })}
                    placeholder="e.g., Main Office, Warehouse, Job Site 1"
                  />
                </div>

                <div className="form-group">
                  <label>Address Line 1 *</label>
                  <input
                    type="text"
                    required
                    autoComplete="street-address"
                    name="address-line1"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    placeholder="Street address"
                  />
                </div>

                <div className="form-group">
                  <label>Address Line 2</label>
                  <input
                    type="text"
                    autoComplete="address-line2"
                    name="address-line2"
                    value={formData.address_line2}
                    onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                    placeholder="Apt, suite, unit, etc."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      required
                      autoComplete="address-level2"
                      name="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="City"
                    />
                  </div>
                  <div className="form-group">
                    <label>State *</label>
                    <input
                      type="text"
                      required
                      autoComplete="address-level1"
                      name="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="State"
                    />
                  </div>
                  <div className="form-group">
                    <label>ZIP Code *</label>
                    <input
                      type="text"
                      required
                      autoComplete="postal-code"
                      name="postal-code"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      placeholder="ZIP"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Country</label>
                  <select
                    autoComplete="country"
                    name="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  >
                    <option value="USA">USA</option>
                    <option value="Canada">Canada</option>
                    <option value="Mexico">Mexico</option>
                  </select>
                </div>

                <div className="form-group">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="primaryAddress"
                      checked={formData.is_primary}
                      onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                    />
                    <label htmlFor="primaryAddress">
                      Primary Address
                    </label>
                  </div>
                  <small className="form-hint">
                    Only one address can be marked as primary. Selecting this will unset any other primary address.
                  </small>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes about this address"
                  />
                </div>
              </div>

              <div className="site-address-modal-footer">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingAddress ? 'Update Address' : 'Save Address'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
